// STARWEAVE — star system. Places a sun + sibling worlds + a moon at vast
// distances around the home planet (which sits at the origin), so flying
// through the system feels enormous. Bodies are deterministic from the system
// seed. Distant worlds are simple lit spheres with an atmosphere glow; the home
// world (the full cube-sphere Planet) lives at the origin, rendered by main.

import * as THREE from 'three';
import { makeAtmosphere } from './planet';

function lcg(seed: number) {
  let s = seed >>> 0;
  return () => { s = (Math.imul(s, 1664525) + 1013904223) >>> 0; return s / 4294967296; };
}

export interface Body {
  name: string;
  obj: THREE.Object3D;
  pos: THREE.Vector3;
  radius: number;
}

// 1 scene unit = ~50 km (so the HUD can show genuinely massive distances).
export const KM_PER_UNIT = 50;

const PALETTE = [0x6fb6ff, 0x4a9a3a, 0xd8a24a, 0xc0341d, 0xcfe8f5, 0x9b5fd0, 0x40c0a0, 0xc88a5a];

export function buildSystem(scene: THREE.Scene, sysSeed: number, sunDir: THREE.Vector3): { bodies: Body[]; sunDir: THREE.Vector3 } {
  const rng = lcg(sysSeed ^ 0x515ee9);
  const sd = sunDir.clone().normalize();
  const bodies: Body[] = [];

  // The star: far, huge, emissive (blooms hard).
  const sunPos = sd.clone().multiplyScalar(280000);
  const sun = new THREE.Mesh(new THREE.SphereGeometry(14000, 40, 40), new THREE.MeshBasicMaterial({ color: 0xfff1c4 }));
  sun.position.copy(sunPos);
  sun.add(makeAtmosphere(17000, new THREE.Color(0xffcf80), 1.6, sd));
  scene.add(sun);
  bodies.push({ name: 'Star', obj: sun, pos: sunPos, radius: 14000 });

  // Sibling worlds on inclined orbits at increasing distances.
  const n = 3 + Math.floor(rng() * 3);
  for (let i = 0; i < n; i++) {
    const dist = 9000 + i * (11000 + rng() * 15000);
    const ang = rng() * Math.PI * 2;
    const incl = (rng() - 0.5) * 0.5;
    const pos = new THREE.Vector3(Math.cos(ang) * dist, Math.sin(incl) * dist, Math.sin(ang) * dist);
    const radius = 320 + rng() * 760;
    const color = new THREE.Color(PALETTE[(rng() * PALETTE.length) | 0]);
    const g = new THREE.Group();
    g.add(new THREE.Mesh(new THREE.SphereGeometry(radius, 28, 22), new THREE.MeshStandardMaterial({ color, roughness: 0.88, metalness: 0.04 })));
    g.add(makeAtmosphere(radius * 1.07, color.clone().lerp(new THREE.Color(0xffffff), 0.35), 0.55, sd));
    g.position.copy(pos);
    scene.add(g);
    bodies.push({ name: `World ${i + 2}`, obj: g, pos, radius });
  }

  // A moon close to the home world (home world is at the origin).
  const mDist = 1500 + rng() * 700;
  const mAng = rng() * Math.PI * 2;
  const mPos = new THREE.Vector3(Math.cos(mAng) * mDist, (rng() - 0.5) * 500, Math.sin(mAng) * mDist);
  const moon = new THREE.Mesh(new THREE.SphereGeometry(48, 24, 18), new THREE.MeshStandardMaterial({ color: 0x9a948c, roughness: 1 }));
  moon.position.copy(mPos);
  scene.add(moon);
  bodies.push({ name: 'Moon', obj: moon, pos: mPos, radius: 48 });

  return { bodies, sunDir: sd };
}

/** Remove a system's bodies from the scene and free their GPU resources. */
export function disposeSystem(scene: THREE.Scene, bodies: Body[]): void {
  for (const b of bodies) {
    b.obj.traverse((o) => {
      const m = o as THREE.Mesh;
      if (m.geometry) m.geometry.dispose();
      const mat = m.material as THREE.Material | THREE.Material[] | undefined;
      if (Array.isArray(mat)) mat.forEach((x) => x.dispose());
      else if (mat) mat.dispose();
    });
    scene.remove(b.obj);
  }
}

/** Nearest body to a point, with surface distance. */
export function nearest(bodies: Body[], p: THREE.Vector3): { name: string; distKm: number } {
  let best = bodies[0];
  let bestD = Infinity;
  for (const b of bodies) {
    const d = p.distanceTo(b.pos) - b.radius;
    if (d < bestD) { bestD = d; best = b; }
  }
  return { name: best.name, distKm: Math.max(0, Math.round(bestD * KM_PER_UNIT)) };
}
